// The host-fed module cache (shell-ia §15.5 F2). Desk frames run with an opaque origin (no allow-same-origin), so the browser keeps no HTTP
// cache for them: every first open of a desk fetched 1–2 MB of the same vendor code (React, page-kit, ECharts) again. The host is a normal
// origin, so it can keep that code: in memory and in Cache Storage, keyed by the immutable vendor URL (`/api/pages/vendor/<set>/<file>`,
// versioned by the set and content-hashed per workspace package). A run document opened with `hostModules` asks the host for the files of
// its import map before it loads them (protocol.ts `ModulesRequestMessage`); the host answers with copies of what it holds, the frame turns
// them into `blob:` URLs inside itself and fetches the rest from the network.
//
// Security (shell-ia §15.5): the host answers only for vendor URLs (public code, no data), it fetches them itself from its own origin, and it
// never stores bytes a frame sent, so no frame can poison another frame's React. A file that resolves URLs against its own location
// (`import.meta`, e.g. MapLibre's worker) is never served from memory: from a `blob:` URL it would break.
import { PROTOCOL_VERSION, type ModulesReply } from "@ncp/page-sdk/protocol";

const CACHE_NAME = "econic-page-modules-v1";
/** Vendor JavaScript only: `/api/pages/vendor/<set>/<file>.js` (stylesheets, fonts and page assets keep their network URLs). */
const VENDOR_JS = /^\/api\/pages\/vendor\/[\w.-]{1,64}\/[\w.@-]+\.(?:m?js)$/;
/** Memory budget for the in-memory copies (Cache Storage keeps everything it holds across reloads). */
const MEMORY_BUDGET = 24 * 1024 * 1024;
const MAX_FILE = 4 * 1024 * 1024;

interface Held {
  bytes: ArrayBuffer;
  /** False when the code reads `import.meta` (it would resolve against a blob: URL). */
  relocatable: boolean;
}

/** The API's origin as the frame sees it (`api.baseUrl`; empty when the API is served from the workspace's own origin). */
let base = "";
export function setModuleBase(apiBaseUrl: string): void {
  base = apiBaseUrl.replace(/\/+$/, "");
}
const baseOrigin = () => {
  try { return base ? new URL(base, window.location.href).origin : window.location.origin; } catch { return window.location.origin; }
};

const memory = new Map<string, Held>();
let memoryBytes = 0;
const fetching = new Map<string, Promise<Held | null>>();
const warmedSets = new Set<string>();
/** The eager modules of the vendor sets warmed so far (`GET /api/pages/vendor-map/{set}`): the only misses the host fetches for itself. */
const eager = new Set<string>();
const decoder = typeof TextDecoder === "undefined" ? null : new TextDecoder();

/** The path of a vendor module URL as the frame wrote it (path-absolute, or absolute on the API's origin), or null. */
export function vendorPath(url: string): string | null {
  try {
    const u = new URL(url, `${baseOrigin()}/`);
    if (u.origin !== baseOrigin() || u.search || u.hash) return null;
    return VENDOR_JS.test(u.pathname) ? u.pathname : null;
  } catch {
    return null;
  }
}

function remember(path: string, held: Held): void {
  if (memory.has(path)) return;
  memory.set(path, held);
  memoryBytes += held.bytes.byteLength;
  // Oldest first out (Map keeps insertion order); a file read again is re-inserted by `touch`.
  for (const [k, v] of memory) {
    if (memoryBytes <= MEMORY_BUDGET) break;
    memory.delete(k);
    memoryBytes -= v.bytes.byteLength;
  }
}

function touch(path: string): Held | undefined {
  const held = memory.get(path);
  if (held) {
    memory.delete(path);
    memory.set(path, held);
  }
  return held;
}

const cacheStorage = (): CacheStorage | null => (typeof caches !== "undefined" && window.isSecureContext ? caches : null);

const relocatable = (bytes: ArrayBuffer) => !decoder || !/\bimport\.meta\b/.test(decoder.decode(bytes));

/** Whether Cache Storage has anything of this set (one lookup), so a first visit does not wait on twenty misses. */
async function storageHolds(): Promise<boolean> {
  const storage = cacheStorage();
  if (!storage) return false;
  try {
    if (!(await storage.has(CACHE_NAME))) return false;
    const cache = await storage.open(CACHE_NAME);
    return (await cache.keys()).length > 0;
  } catch {
    return false;
  }
}

async function fromStorage(path: string): Promise<Held | null> {
  const storage = cacheStorage();
  if (!storage) return null;
  try {
    const cache = await storage.open(CACHE_NAME);
    const hit = await cache.match(path);
    if (!hit) return null;
    const bytes = await hit.arrayBuffer();
    const held = { bytes, relocatable: hit.headers.get("x-econic-relocatable") !== "0" };
    remember(path, held);
    return held;
  } catch {
    return null;
  }
}

/** Fetches one vendor file from the host's own origin and keeps it (memory + Cache Storage). */
function fetchAndKeep(path: string): Promise<Held | null> {
  const running = fetching.get(path);
  if (running) return running;
  const run = (async () => {
    try {
      const res = await fetch(`${base}${path}`, { credentials: "omit", cache: "force-cache" });
      const type = res.headers.get("content-type") ?? "";
      if (!res.ok || !/javascript|ecmascript/i.test(type)) return null;
      const bytes = await res.arrayBuffer();
      if (bytes.byteLength === 0 || bytes.byteLength > MAX_FILE) return null;
      const held = { bytes, relocatable: relocatable(bytes) };
      remember(path, held);
      const storage = cacheStorage();
      if (storage) {
        try {
          const cache = await storage.open(CACHE_NAME);
          await cache.put(path, new Response(bytes.slice(0), { headers: { "content-type": type, "x-econic-relocatable": held.relocatable ? "1" : "0" } }));
        } catch {
          // storage full or blocked: memory still has it
        }
      }
      return held;
    } catch {
      return null;
    } finally {
      fetching.delete(path);
    }
  })();
  fetching.set(path, run);
  return run;
}

const idle = (fn: () => void) => {
  const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
  if (w.requestIdleCallback) w.requestIdleCallback(fn, { timeout: 4000 });
  else setTimeout(fn, 200);
};

/**
 * The files the host holds of those a frame asked for, as copies the reply moves to the frame. Reads memory first, then Cache Storage;
 * never the network (the frame does not wait on it). Files it does not hold are left out and fetched in the background for the next frame.
 */
/** How long the frame may wait on Cache Storage for its modules; whatever is not read by then loads from the network. */
const STORAGE_BUDGET_MS = 80;

export async function modulesFor(urls: readonly string[]): Promise<ModulesReply> {
  const files: Record<string, ArrayBuffer> = {};
  const missing: string[] = [];
  const paths = urls.slice(0, 200).map((url) => [url, vendorPath(url)] as const).filter((x): x is readonly [string, string] => !!x[1]);
  // Memory first (instant); Cache Storage only within a small budget, and not at all when it holds none of them (a first visit).
  const pending: (readonly [string, string])[] = [];
  for (const [url, path] of paths) {
    const held = touch(path);
    if (held) { if (held.relocatable) files[url] = held.bytes.slice(0); }
    else pending.push([url, path]);
  }
  if (pending.length && (await storageHolds())) {
    const budget = new Promise<"late">((r) => setTimeout(() => r("late"), STORAGE_BUDGET_MS));
    await Promise.all(pending.map(async ([url, path]) => {
      const held = await Promise.race([fromStorage(path), budget]);
      if (held === "late" || !held) { missing.push(path); return; }
      if (held.relocatable && !files[url]) files[url] = held.bytes.slice(0);
    }));
  } else {
    missing.push(...pending.map(([, p]) => p));
  }
  // A miss is fetched for the next frame only when it is one of the set's eager modules; lazy chunks (editors, maps, 3D) stay network-only.
  const wanted = missing.filter((p) => eager.has(p));
  if (wanted.length) idle(() => void learn(wanted));
  return { ncp: PROTOCOL_VERSION, kind: "modules", files };
}

// A frame that is loading has the network first: the host's own fetches of the same code would share the link with it (on a 20 Mbps
// line the first open got slower). Learning waits until no frame is between its session and its first view.
let loadingFrames = 0;
/** PageHost reports a frame starting to load (true) and reaching its first view, failing or closing (false). */
export function frameLoading(on: boolean): void {
  loadingFrames = Math.max(0, loadingFrames + (on ? 1 : -1));
}
const quiet = async () => {
  for (let waited = 0; loadingFrames > 0 && waited < 30_000; waited += 300) await new Promise((r) => setTimeout(r, 300));
};

/** Fetches and keeps vendor files a frame is about to need (its import map's misses), two at a time, once no frame is loading. */
export async function learn(paths: readonly string[]): Promise<void> {
  const queue = paths.map((p) => vendorPath(p)).filter((p): p is string => !!p && !memory.has(p));
  const worker = async () => {
    for (let next = queue.shift(); next; next = queue.shift()) {
      if (memory.has(next) || (await fromStorage(next))) continue;
      await quiet();
      await fetchAndKeep(next);
    }
  };
  await Promise.all([worker(), worker()]);
}

/**
 * Warms a vendor set once per set version, at idle after sign-in (F2: "warmVendor(set)"): the files every page loads first. `files` are
 * the set's module URLs from `GET /api/pages/vendor-map/{set}`; eager modules go first, lazy chunks (editors, maps, 3D) are left out.
 */
export function warmVendor(set: string, files: readonly string[]): void {
  if (warmedSets.has(set)) return;
  warmedSets.add(set);
  for (const f of files) {
    const p = vendorPath(f);
    if (p) eager.add(p);
  }
  idle(() => void learn(files));
}

/** True when the host can feed modules at all (a window with fetch; Cache Storage is optional). */
export const moduleCacheAvailable = (): boolean => typeof window !== "undefined" && typeof fetch === "function";

/** For tests and the perf harness: what the cache holds. */
export function moduleCacheStats(): { files: number; bytes: number; sets: string[] } {
  return { files: memory.size, bytes: memoryBytes, sets: [...warmedSets] };
}
